<section class="appointment-detail">
  @if (isLoading()) {
  <div class="state-panel" role="status" aria-live="polite">
    <app-progress-spinner [diameter]="56" />
    <h1 class="state-panel__title">Loading appointments…</h1>
    <p class="state-panel__message">Hang tight while we fetch this access event.</p>
  </div>
  } @else if (loadError()) {
  <div class="state-panel" role="alert">
    <span
      class="state-panel__icon"
      [class.state-panel__icon--error]="!notFound()"
      aria-hidden="true"
    >
      <app-icon
        [name]="errorIcon()"
        size="custom"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
    </span>
    <h1 class="state-panel__title">{{ errorTitle() }}</h1>
    <p class="state-panel__message">{{ loadError() }}</p>
    <a class="primary-button state-panel__button fw-600" routerLink="/activity-logs">
      <app-icon
        name="chevron_left"
        size="sm"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <span>Back to Audit Log</span>
    </a>
  </div>
  } @else if (detail()) {
  <nav class="breadcrumb flex align-center gap-8 fs-12 mb-15" aria-label="Breadcrumb">
    <a
      class="breadcrumb__link fw-600"
      routerLink="/activity-logs"
      aria-label="Return to the audit log"
      >Audit Log</a
    >
    @if (patientName()) {
    <app-icon
      class="breadcrumb__icon"
      name="chevron_right"
      size="xs"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <!-- Patient crumb: links to the patient's Access & Controls screen when the id is usable. -->
    @if (patientAccessRoute(); as route) {
    <a class="breadcrumb__link fw-600" [routerLink]="route">{{ patientName() }}</a>
    } @else {
    <span>{{ patientName() }}</span>
    } }
    <app-icon
      class="breadcrumb__icon"
      name="chevron_right"
      size="xs"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <span aria-current="page">{{ featureArea() }} — {{ eventDate() }}</span>
  </nav>

  <header class="page-header flex justify-between align-start gap-20">
    <div class="page-header__heading">
      <div class="flex align-center gap-8">
        <h1 class="page-title fs-22 fw-700 m-0">{{ featureArea() }}</h1>
        @if (eventBadge(); as eventBadge) {
        <span class="action-badge" [class]="eventBadgeClass()">{{ eventBadge }}</span>
        }
      </div>
      <p class="page-subtitle">{{ subtitle() }}</p>
    </div>

    <a class="secondary-button page-header__back fw-600" routerLink="/activity-logs">
      <app-icon
        name="chevron_left"
        size="sm"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <span>Back to Audit Log</span>
    </a>
  </header>

  @if (severityNote(); as severityNote) {
  <div class="severity-banner flex align-center gap-8" role="note">
    <app-icon
      class="severity-banner__icon"
      name="warning"
      size="sm"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <span>{{ severityNote }}</span>
  </div>
  }

  <section class="detail-card" aria-labelledby="event-details-title">
    <div class="detail-card__header">
      <h2 id="event-details-title" class="detail-card__title">Event Details</h2>
    </div>

    <div class="detail-card__body event-grid">
      <div class="field">
        <span class="field__label">Date &amp; Time</span>
        <span class="field__value">{{ eventDate() }}</span>
        <span class="field__secondary">{{ eventTime() }}</span>
      </div>

      <div class="field">
        <span class="field__label">Patient</span>
        <!-- The name links to the patient's Access & Controls screen, where their access can be
             acted on. The account status chip rides with it: staff reviewing what was disclosed
             about a patient have to see that the account has since been blocked, and a screen
             that showed the name alone would read as though nothing had changed. -->
        <span class="field__value flex align-center flex-wrap gap-8">
          @if (patientName() && patientAccessRoute(); as route) {
          <a class="patient-link fw-600" [routerLink]="route">{{ patientName() }}</a>
          } @else {
          <span>{{ patientName() || '—' }}</span>
          } @if (hasAccessStatusSettled()) {
          <span
            class="badge badge--compact {{ accessStatusBadge().class }}"
            [attr.title]="accessStatusTitle()"
            >{{ accessStatusBadge().label }}</span
          >
          }
        </span>
        <!--
          Both are documented as always populated. If the name isn't, an em-dash beats a blank
          value; if the MRN isn't, the whole line goes — a bare "MRN:" reads as a label for a
          number that is there but unreadable, which on an audit surface is worse than saying
          nothing.
        -->
        @if (medicalRecordNumber()) {
        <span class="field__secondary">MRN: {{ medicalRecordNumber() }}</span>
        }
      </div>

      @if (actorName()) {
      <div class="field">
        <span class="field__label">Performed By</span>
        <span class="field__value">{{ actorName() }}</span>
        <span class="field__secondary">{{ actorRole() }}</span>
      </div>
      }
    </div>
  </section>

  <section class="records-card" aria-labelledby="appointments-title">
    <div class="records-card__header flex justify-between align-center gap-8">
      <h2 id="appointments-title" class="records-card__title">
        Appointments Visible During Session
      </h2>
      <span class="records-card__count fs-12"
        >{{ rows().length }} {{ rows().length === 1 ? 'entry' : 'entries' }}</span
      >
    </div>

    @if (rows().length === 0) {
    <div class="records-status flex flex-col align-center justify-center text-center" role="status">
      <h3>No appointments to display</h3>
      @if (fallbackDetailRoute(); as fallbackDetailRoute) {
      <!--
        The event hydrated records, just not appointment-shaped ones — so this screen was the wrong
        destination for it, not the end of the trail. See `AppointmentDetail.fallbackDetailRoute`.
      -->
      <p>This access event disclosed records that aren't appointments.</p>
      <a class="secondary-button fw-600" [routerLink]="fallbackDetailRoute">
        <span>View disclosed records</span>
      </a>
      } @else {
      <p>This access event has no individual appointments recorded against it.</p>
      }
    </div>
    } @else {
    <!--
      Focusable wrapper, not the table itself: below 900px the grid is wider than the card and
      scrolls horizontally, and a scroll region with no focusable content is unreachable by
      keyboard. The affordances are bound to a measurement rather than always on — above the
      breakpoint nothing scrolls, and a tab stop that does nothing is its own accessibility defect.
      `tabindex` stays off the `role="table"` element so the grid semantics are unchanged.
    -->
    <div
      #tableScroll
      class="records-table__scroll"
      [attr.role]="isScrollable() ? 'region' : null"
      [attr.aria-label]="isScrollable() ? 'Appointments table, scrollable' : null"
      [attr.tabindex]="isScrollable() ? 0 : null"
    >
      <div class="records-table" role="table" aria-label="Appointments visible during this session">
        <div class="records-table__header records-table__grid gap-12 fw-600 uppercase" role="row">
          <div role="columnheader">Appointment Date</div>
          <div role="columnheader">Provider</div>
          <div role="columnheader">Type / Specialty</div>
          <div role="columnheader">Location</div>
          <div role="columnheader">Status</div>
        </div>

        <div class="records-table__body" role="rowgroup">
          @for (row of rows(); track row.trackKey) {
          <div
            class="records-table__row records-table__grid gap-12"
            [class.records-table__row--deleted]="row.isDeleted"
            role="row"
          >
            <div class="cell" role="cell">
              <span class="cell__primary">{{ row.date }}</span>
              @if (row.time) {
              <span class="cell__secondary">{{ row.time }}</span>
              }
              <!--
              Stated in text, not just as the row's muted colour: that a disclosed record has since
              been deleted is a fact of the audit trail, and colour alone would hide it from screen
              readers and from anyone not comparing rows side by side.
            -->
              @if (row.isDeleted) {
              <span class="status-chip status-chip--deleted">Deleted</span>
              }
            </div>

            <div class="cell" role="cell">
              <span class="cell__primary">{{ row.providerName }}</span>
              <!--
              No source table carries a specialty, so this normally renders nothing. It is not
              dashed: a dash on every row would advertise missing data rather than an absent field.
            -->
              @if (row.providerSpecialty) {
              <span class="cell__secondary">{{ row.providerSpecialty }}</span>
              }
            </div>

            <div class="cell cell--type" role="cell">
              <span class="cell__primary">{{ row.appointmentType }}</span>
            </div>

            <div class="cell" role="cell">
              <span class="cell__primary">{{ row.locationName }}</span>
              @if (row.locationDetail) {
              <span class="cell__secondary">{{ row.locationDetail }}</span>
              }
            </div>

            <div class="cell cell--status" role="cell">
              <!--
              Display-only, never interactive. The chip is a display grouping the API resolves; when
              it declines to categorise a status the exact label still renders, without a chip.
            -->
              @if (row.statusKind) {
              <span class="badge badge--compact" [class]="'badge--' + row.statusKind"
                >{{ row.statusLabel }}</span
              >
              } @else {
              <span class="cell__secondary">{{ row.statusLabel }}</span>
              }
            </div>
          </div>
          }
        </div>
      </div>
    </div>
    }
  </section>
  }
</section>
